/**
 * Tenant View Page
 *
 * Page for viewing tenant details.
 *
 * @fileoverview Admin tenant view page
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  SimpleGrid,
  Divider,
  useColorModeValue,
  useDisclosure,
  useToast,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  MdEdit,
  MdDelete,
  MdCheckCircle,
  MdCancel,
  MdArrowBack,
  MdBusiness,
  MdPerson,
  MdLocationOn,
  MdPhone,
  MdEmail,
  MdLanguage,
  MdAccountBalance,
  MdRefresh,
  MdLinkOff,
} from 'react-icons/md';
import { DeleteTenantModal } from '../../../components/admin';
import { useAdminTenant } from '../../../hooks/useAdminTenants';
import { adminApi } from '../../../services/adminApi';
import { toUserMessage } from '../../../lib/apiError';

interface InfoItemProps {
  icon: React.ElementType;
  label: string;
  value: string | undefined | null;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon, label, value }) => {
  if (!value) return null;

  return (
    <HStack align="start" spacing={3}>
      <Icon as={icon} color="gray.400" boxSize={5} mt={0.5} />
      <Box>
        <Text fontSize="xs" color="gray.500" textTransform="uppercase">
          {label}
        </Text>
        <Text>{value}</Text>
      </Box>
    </HStack>
  );
};

/**
 * Tenant View Page Component
 *
 * Displays full tenant details with actions.
 */
export const TenantView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const deleteModal = useDisclosure();

  const {
    tenant,
    loading,
    error,
    refetch,
  } = useAdminTenant(id!);

  const [actionLoading, setActionLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [plaidTestLoading, setPlaidTestLoading] = useState(false);
  const [plaidDisconnectLoading, setPlaidDisconnectLoading] = useState(false);
  const [reconnectEmailLoading, setReconnectEmailLoading] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingEmailError, setPendingEmailError] = useState('');
  const [emailSaveLoading, setEmailSaveLoading] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Handle activate
  const handleActivate = async () => {
    if (!tenant) return;
    setActionLoading(true);
    try {
      await adminApi.tenants.activate(tenant.id);
      toast({
        title: 'Tenant activated',
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't activate the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle deactivate
  const handleDeactivate = async () => {
    if (!tenant) return;
    setActionLoading(true);
    try {
      await adminApi.tenants.deactivate(tenant.id);
      toast({
        title: 'Tenant deactivated',
        status: 'warning',
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't deactivate the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const handleDeleteConfirm = async () => {
    if (!tenant) return;
    setDeleteLoading(true);
    try {
      await adminApi.tenants.delete(tenant.id);
      toast({
        title: 'Tenant deleted',
        description: `${tenant.name} has been deleted.`,
        status: 'success',
        duration: 3000,
      });
      navigate('/admin/tenants');
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't delete the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDeleteLoading(false);
      deleteModal.onClose();
    }
  };

  // Handle Plaid test connection
  const handleTestPlaidConnection = async () => {
    if (!tenant) return;
    setPlaidTestLoading(true);
    try {
      const result = await adminApi.tenants.testPlaidConnection(tenant.id);
      toast({
        title: result.success ? 'Connection Active' : 'Connection Issue',
        description: result.message,
        status: result.success ? 'success' : 'warning',
        duration: 5000,
      });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't test the connection. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setPlaidTestLoading(false);
    }
  };

  // Handle Plaid disconnect
  const handleDisconnectPlaid = async () => {
    if (!tenant) return;
    setPlaidDisconnectLoading(true);
    try {
      await adminApi.tenants.disconnectPlaid(tenant.id);
      toast({
        title: 'Bank Disconnected',
        description: 'The bank connection has been removed.',
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't disconnect the bank. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setPlaidDisconnectLoading(false);
    }
  };
   
  // Send reconnect email (assumes tenant already has managerEmail)
  const doSendReconnectEmail = async () => {
    if (!tenant) return;
    setReconnectEmailLoading(true);
    try {
      const result = await adminApi.tenants.sendReconnectEmail(tenant.id);
      toast({
        title: 'Email Sent',
        description: result.message || 'Reconnection email has been sent.',
        status: 'success',
        duration: 5000,
      });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't send the email. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setReconnectEmailLoading(false);
    }
  };

  // Handle Send Reconnect Email — asks for email first if tenant has none
  const handleSendReconnectEmail = () => {
    if (!tenant) return;
    if (!tenant.managerEmail) {
      setPendingEmail('');
      setPendingEmailError('');
      setEmailModal(true);
    } else {
      doSendReconnectEmail();
    }
  };

  // Save email and send reconnect email
  const handleEmailModalConfirm = async () => {
    const email = pendingEmail.trim();
    if (!email) {
      setPendingEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setPendingEmailError('Must be a valid email address');
      return;
    }
    setEmailSaveLoading(true);
    try {
      await adminApi.tenants.update(tenant!.id, { managerEmail: email });
      refetch();
      setEmailModal(false);
      await doSendReconnectEmail();
    } catch (err) {
      toast({
        title: 'Error saving email',
        description: toUserMessage(err, "We couldn't save the email. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setEmailSaveLoading(false);
    }
  };

  // Get status color for Plaid badge
  const getPlaidStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'LOGIN_REQUIRED':
        return 'orange';
      case 'REVOKED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <Skeleton h="32px" w="200px" mb={2} />
            <Skeleton h="20px" w="100px" />
          </Box>
          <HStack>
            <Skeleton h="40px" w="100px" />
            <Skeleton h="40px" w="100px" />
          </HStack>
        </HStack>
        <Skeleton h="400px" borderRadius="lg" />
      </VStack>
    );
  }

  // Error state
  if (error) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/tenants')}
        >
          Back to Tenants
        </Button>
      </VStack>
    );
  }

  // Not found
  if (!tenant) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>Tenant not found</AlertDescription>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/tenants')}
        >
          Back to Tenants
        </Button>
      </VStack>
    );
  }

  // Format address
  const addressParts = [
    tenant.address,
    tenant.addressLine2,
    [tenant.city, tenant.state, tenant.zipCode].filter(Boolean).join(', '),
    tenant.country,
  ].filter(Boolean);
  const fullAddress = addressParts.length > 0 ? addressParts.join('\n') : null;

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <HStack spacing={4}>
          <Button
            variant="ghost"
            leftIcon={<Icon as={MdArrowBack} />}
            onClick={() => navigate('/admin/tenants')}
          >
            Back
          </Button>
          <Box>
            <HStack>
              <Heading size="lg">{tenant.name}</Heading>
              <Badge
                colorScheme={tenant.isActive ? 'green' : 'red'}
                fontSize="sm"
                px={2}
                py={1}
              >
                {tenant.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </HStack>
            <Text color="gray.500" fontFamily="mono">
              {tenant.slug}
            </Text>
          </Box>
        </HStack>
        <HStack>
          {tenant.isActive ? (
            <Button
              leftIcon={<Icon as={MdCancel} />}
              variant="outline"
              colorScheme="orange"
              onClick={handleDeactivate}
              isLoading={actionLoading}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              leftIcon={<Icon as={MdCheckCircle} />}
              variant="outline"
              colorScheme="green"
              onClick={handleActivate}
              isLoading={actionLoading}
            >
              Activate
            </Button>
          )}
          <Button
            leftIcon={<Icon as={MdEdit} />}
            colorScheme="blue"
            onClick={() => navigate(`/admin/tenants/${tenant.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            leftIcon={<Icon as={MdDelete} />}
            colorScheme="red"
            variant="outline"
            onClick={deleteModal.onOpen}
          >
            Delete
          </Button>
        </HStack>
      </HStack>

      {/* Content */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Business Info */}
        <Box
          bg={bgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          p={6}
        >
          <HStack mb={4}>
            <Icon as={MdBusiness} color="blue.500" boxSize={5} />
            <Heading size="sm">Business Information</Heading>
          </HStack>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Tenant ID
              </Text>
              <Text fontFamily="mono" fontSize="sm">
                {tenant.id}
              </Text>
            </Box>
            {tenant.ein && (
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  EIN
                </Text>
                <Text>{tenant.ein}</Text>
              </Box>
            )}
            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Members
              </Text>
              <Text>{tenant._count?.members ?? 0}</Text>
            </Box>
          </VStack>
        </Box>

        {/* Contact Info */}
        <Box
          bg={bgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          p={6}
        >
          <HStack mb={4}>
            <Icon as={MdPerson} color="green.500" boxSize={5} />
            <Heading size="sm">Contact Information</Heading>
          </HStack>
          <VStack align="stretch" spacing={4}>
            <InfoItem
              icon={MdPerson}
              label="Manager"
              value={tenant.managerName}
            />
            <InfoItem
              icon={MdEmail}
              label="Email"
              value={tenant.managerEmail}
            />
            <InfoItem
              icon={MdPhone}
              label="Phone"
              value={tenant.phoneNumber}
            />
            <InfoItem
              icon={MdLanguage}
              label="Website"
              value={tenant.website}
            />
          </VStack>
        </Box>

        {/* Address */}
        {fullAddress && (
          <Box
            bg={bgColor}
            borderRadius="lg"
            border="1px"
            borderColor={borderColor}
            p={6}
          >
            <HStack mb={4}>
              <Icon as={MdLocationOn} color="red.500" boxSize={5} />
              <Heading size="sm">Address</Heading>
            </HStack>
            <Text whiteSpace="pre-line">{fullAddress}</Text>
          </Box>
        )}

        {/* Bank Connection (Plaid) */}
        <Box
          bg={bgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          p={6}
        >
          <HStack mb={4}>
            <Icon as={MdAccountBalance} color="purple.500" boxSize={5} />
            <Heading size="sm">Bank Connection</Heading>
          </HStack>
          {(tenant.plaidTokens?.length ?? 0) > 0 ? (
            <VStack align="stretch" spacing={4} divider={<Divider />}>
              {(tenant.plaidTokens ?? []).map((token) => (
                <VStack key={token.id} align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <Text fontWeight="medium">
                      {token.institutionName || 'Unknown bank'}
                    </Text>
                    <Badge colorScheme={getPlaidStatusColor(token.status)}>
                      {token.status}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Connected {formatDate(token.createdAt)}
                  </Text>
                  {token.status === 'LOGIN_REQUIRED' && (
                    <Button
                      size="xs"
                      leftIcon={<Icon as={MdEmail} />}
                      colorScheme="orange"
                      variant="outline"
                      onClick={handleSendReconnectEmail}
                      isLoading={reconnectEmailLoading}
                      alignSelf="start"
                    >
                      Send Reconnect Email
                    </Button>
                  )}
                </VStack>
              ))}
              <Divider />
              <HStack spacing={3}>
                <Button
                  size="sm"
                  leftIcon={<Icon as={MdRefresh} />}
                  variant="outline"
                  onClick={handleTestPlaidConnection}
                  isLoading={plaidTestLoading}
                >
                  Test
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Icon as={MdLinkOff} />}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDisconnectPlaid}
                  isLoading={plaidDisconnectLoading}
                >
                  Disconnect
                </Button>
              </HStack>
            </VStack>
          ) : (
            <VStack align="center" spacing={3} py={4}>
              <Icon as={MdAccountBalance} boxSize={10} color="gray.300" />
              <Text color="gray.500" textAlign="center">
                No bank connected
              </Text>
              <Text fontSize="sm" color="gray.400" textAlign="center" mb={2}>
                Tenant has not connected a bank account yet
              </Text>
              <Button
                size="sm"
                leftIcon={<Icon as={MdEmail} />}
                colorScheme="blue"
                onClick={handleSendReconnectEmail}
                isLoading={reconnectEmailLoading}
              >
                Send Connection Email
              </Button>
            </VStack>
          )}
        </Box>

        {/* Timestamps */}
        <Box
          bg={bgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          p={6}
        >
          <Heading size="sm" mb={4}>Timeline</Heading>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Created
              </Text>
              <Text>{formatDate(tenant.createdAt)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Last Updated
              </Text>
              <Text>{formatDate(tenant.updatedAt)}</Text>
            </Box>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Delete Modal */}
      <DeleteTenantModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        tenant={tenant}
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
      />

      {/* Email Required Modal */}
      <Modal isOpen={emailModal} onClose={() => setEmailModal(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Email required</ModalHeader>
          <ModalBody>
            <Text mb={4} color="gray.600" fontSize="sm">
              This tenant has no email address on file. Enter an email to save it and send the reconnection email.
            </Text>
            <FormControl isRequired isInvalid={!!pendingEmailError}>
              <FormLabel>Email address</FormLabel>
              <Input
                type="email"
                placeholder="manager@company.com"
                value={pendingEmail}
                onChange={(e) => {
                  setPendingEmail(e.target.value);
                  setPendingEmailError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailModalConfirm()}
                autoFocus
              />
              <FormErrorMessage>{pendingEmailError}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={() => setEmailModal(false)} isDisabled={emailSaveLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleEmailModalConfirm}
              isLoading={emailSaveLoading}
              loadingText="Saving..."
            >
              Save & Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default TenantView;
